/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * An active project skill. All API reads return active skills, and archive returns an empty response.
 */
export type Skill = {
  /**
   * The skill classification.
   */
  classification: string;
  /**
   * When the skill was created.
   */
  createdAt: Date;
  /**
   * The user-facing registry name.
   */
  displayName: string;
  /**
   * When this skill was first activated.
   */
  firstSeenAt?: Date | undefined;
  /**
   * Whether the skill has at least one valid version available to distribute.
   */
  hasValidVersion: boolean;
  /**
   * The skill ID.
   */
  id: string;
  /**
   * When an agent last loaded the skill over MCP from a Speakeasy-hosted server, absent when it never has. Returned by list and get only.
   */
  lastLoadedOverMcpAt?: Date | undefined;
  /**
   * When this skill was most recently activated.
   */
  lastSeenAt?: Date | undefined;
  /**
   * The current version ID, selected by effective promotion time.
   */
  latestVersionId?: string | undefined;
  /**
   * The normalized project-unique skill name.
   */
  name: string;
  /**
   * The project that owns the skill.
   */
  projectId: string;
  /**
   * The number of reconciled activations observed for this skill.
   */
  seenCount: number;
  /**
   * The active public share token, absent when the skill is not shared.
   */
  shareToken?: string | undefined;
  /**
   * How the skill entered the registry.
   */
  sourceKind: string;
  /**
   * The optional registry summary.
   */
  summary?: string | undefined;
  /**
   * Registry tags for categorizing the skill.
   */
  tags: Array<string>;
  /**
   * When the skill was last updated.
   */
  updatedAt: Date;
  /**
   * The number of immutable versions recorded for the skill.
   */
  versionCount: number;
};

/** @internal */
export const Skill$inboundSchema: z.ZodMiniType<Skill, unknown> = z.pipe(
  z.object({
    classification: z.string(),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    display_name: z.string(),
    first_seen_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    has_valid_version: z.boolean(),
    id: z.string(),
    last_loaded_over_mcp_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_seen_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    latest_version_id: z.optional(z.string()),
    name: z.string(),
    project_id: z.string(),
    seen_count: z.int(),
    share_token: z.optional(z.string()),
    source_kind: z.string(),
    summary: z.optional(z.string()),
    tags: z.array(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    version_count: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "display_name": "displayName",
      "first_seen_at": "firstSeenAt",
      "has_valid_version": "hasValidVersion",
      "last_loaded_over_mcp_at": "lastLoadedOverMcpAt",
      "last_seen_at": "lastSeenAt",
      "latest_version_id": "latestVersionId",
      "project_id": "projectId",
      "seen_count": "seenCount",
      "share_token": "shareToken",
      "source_kind": "sourceKind",
      "updated_at": "updatedAt",
      "version_count": "versionCount",
    });
  }),
);

export function skillFromJSON(
  jsonString: string,
): SafeParseResult<Skill, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Skill$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Skill' from JSON`,
  );
}
